import { supabase } from '../config/supabase';

// Create a fetch wrapper that handles session timeouts
export const fetchWithAuth = async (url, options = {}) => {
  try {
    // Get current session
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error || !session) {
      throw new Error('No valid session');
    }

    // Add authorization header
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
      'Authorization': `Bearer ${session.access_token}`
    };

    // Make the request
    const response = await fetch(url, {
      ...options,
      headers
    });

    // Check for session timeout responses
    if (response.status === 401) {
      const data = await response.json();
      if (data.code === 'SESSION_EXPIRED' || data.code === 'SESSION_TIMEOUT') {
        // Session expired, sign out user
        try {
          await supabase.auth.signOut({ scope: 'local' });
        } catch (error) {
          console.log('SignOut error during session timeout (expected):', error.message);
        }
        alert(data.message || 'Your session has expired. Please log in again.');
        window.location.href = '/login';
        return;
      }
    }

    return response;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Interceptor for handling session timeouts in existing fetch calls
export const setupApiInterceptor = () => {
  // Store original fetch
  const originalFetch = window.fetch;
  
  // Override fetch globally
  window.fetch = async (url, options = {}) => {
    // Only intercept API calls to our backend
    const isApiCall = url.includes('/api/') || url.includes('localhost:3001');
    
    if (!isApiCall) {
      return originalFetch(url, options);
    }

    try {
      const response = await originalFetch(url, options);
      
      // Check for session timeout responses
      if (response.status === 401) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          if (data.code === 'SESSION_EXPIRED' || data.code === 'SESSION_TIMEOUT') {
            // Session expired, sign out user
            try {
              await supabase.auth.signOut({ scope: 'local' });
            } catch (error) {
              console.log('SignOut error during session timeout (expected):', error.message);
            }
            alert(data.message || 'Your session has expired. Please log in again.');
            window.location.href = '/login';
            return response;
          }
        }
      }

      return response;
    } catch (error) {
      // Don't log AbortErrors as they are intentional (timeouts, user cancellations)
      if (error.name !== 'AbortError') {
        console.error('API request intercepted error:', error);
      } else {
        console.log('API request aborted (intentional timeout or cancellation)');
      }
      throw error;
    }
  };
};

// Restore original fetch (useful for cleanup)
export const restoreOriginalFetch = () => {
  // This would need to store the original fetch reference
  // For now, reloading the page will restore it
  window.location.reload();
};
